import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../supabaseClient';
import OfflineStatusBanner from './OfflineStatusBanner/OfflineStatusBanner';
import { useDispatch } from 'react-redux';
import { addBasketToWorkspace } from '../store/workspacesSlice';

const AddBasketModal = ({ isOpen, onClose, workspaceId, onAdd, initialBasketName = '' }) => {
  const dispatch = useDispatch();
  const [basketName, setBasketName] = useState(initialBasketName);
  const [basketDescription, setBasketDescription] = useState('');
  const [error, setError] = useState(null);
  const basketNameInputRef = useRef(null);

  useEffect(() => {
    setError(null);
    setBasketName(initialBasketName);
    if (isOpen && basketNameInputRef.current) {
      setTimeout(() => {
        basketNameInputRef.current.focus();
      }, 50);
    }
  }, [isOpen, initialBasketName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!workspaceId) {
      setError('Workspace ID is missing. Please try again.');
      console.error('Cannot add basket: workspaceId is undefined');
      return;
    }

    try {
      const basketData = {
        name: basketName,
        description: basketDescription,
        workspace_id: workspaceId,
      };

      await onAdd(basketData);
      
      onClose();
      setBasketName('');
      setBasketDescription('');
    } catch (error) {
      console.error('Error adding basket:', error);
      setError(error.message || 'Failed to add basket. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-neutral-50 rounded-xl border-2 border-neutral-900 shadow-xl p-6 w-full max-w-md">
        <OfflineStatusBanner />
        
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-neutral-900">Add New Basket</h2>
          <button onClick={onClose} className="text-neutral-900 hover:text-neutral-600">
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="basketName" className="block text-sm font-medium text-neutral-900 mb-1">
              Basket Name 
            </label>
            <input
              id="basketName"
              ref={basketNameInputRef}
              type="text"
              value={basketName}
              onChange={(e) => setBasketName(e.target.value)}
              placeholder="Pick a short, distinct name."
              className="w-full px-3 py-2 bg-white border-2 border-neutral-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-cranberry-700 focus:border-transparent"
              required
            />
            <p className="mt-1.5 text-sm text-neutral-600">
              This will be your <strong><u>memory trigger</u></strong>. Examples: "green", "48 hours", "strawberry"
            </p>
          </div>
          <div>
            <label htmlFor="basketDescription" className="block text-sm font-medium text-neutral-900 mb-1">
              Description (optional)
            </label>
            <textarea
              id="basketDescription"
              value={basketDescription}
              onChange={(e) => setBasketDescription(e.target.value)}
              placeholder="e.g., for topics I want to associate with '48 hours'/'hearing loss'/'skips'/'green'"
              className="w-full px-3 py-2 bg-white border-2 border-neutral-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-cranberry-700 focus:border-transparent h-24"
            />
          </div>
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white text-neutral-900 rounded-xl border-2 border-neutral-900 font-medium hover:bg-neutral-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-cranberry-700 text-white rounded-xl border-2 border-neutral-900 font-medium hover:bg-cranberry-600 transition-colors"
            >
              Add Basket
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBasketModal;
